import { useMemo, useCallback, useEffect, useState } from 'react';
import navigation from './navigation';
import { useTranslation } from 'react-i18next';
import routes from './routes';
import { getOwnerPlanProfile } from 'src/services/plan-service';
import { OwnerPlanFeatureFlags, OwnerPlanProfile } from 'src/state/api-models/plan-subscription';
import { OwnerPlan } from 'src/guards/owner-plans';
import { getTokenValue } from 'src/state/ducks/common/auth/storage';

const ADVANCED_REPORT_SCREENS = ['daily_report', 'store_report', 'service_product_report'];
const MODULE_FEATURE_MAP: Partial<Record<string, keyof OwnerPlanFeatureFlags>> = {
  sale: 'pos',
  schedule: 'appointment',
  walkin: 'walkin',
  turn_system: 'turn_system',
  giftcard: 'giftcard',
  membership: 'membership',
  sms: 'sms',
  emarketing: 'emarketing',
};
const SCREEN_FEATURE_MAP: Partial<Record<string, keyof OwnerPlanFeatureFlags>> = {
  payroll_report: 'payroll',
};

export const useAppRoutes = () => {
  const { t } = useTranslation('navigation');
  const [advancedReportsEnabled, setAdvancedReportsEnabled] = useState<boolean | null>(null);
  const [planFeatures, setPlanFeatures] = useState<OwnerPlanFeatureFlags | null>(null);

  useEffect(() => {
    // Only fetch plan profile if user is authenticated (has token)
    const token = getTokenValue();
    if (!token) {
      setAdvancedReportsEnabled(null);
      setPlanFeatures(null);
      return;
    }

    // Defer plan profile fetch to avoid blocking initial render
    // Use requestIdleCallback if available, otherwise setTimeout
    let isMounted = true;
    const fetchPlanProfile = async () => {
      try {
        const response = await getOwnerPlanProfile();
        if (!isMounted) {
          return;
        }
        const profile: OwnerPlanProfile | undefined = response.data?.data;
        if (!profile) {
          setAdvancedReportsEnabled(null);
          setPlanFeatures(null);
          return;
        }

        const explicitFlag = profile.features?.advanced_reports;
        if (typeof explicitFlag === 'boolean') {
          setAdvancedReportsEnabled(explicitFlag);
        } else {
          // Fallback: only Enterprise (plan 4) has advanced reports by default
          setAdvancedReportsEnabled(profile.plan === OwnerPlan.Enterprise);
        }

        if (profile.features) {
          setPlanFeatures(profile.features);
        } else {
          setPlanFeatures(null);
        }
      } catch (error) {
        if (isMounted) {
          setAdvancedReportsEnabled(null);
          setPlanFeatures(null);
        }
        
        // Log SSL errors for debugging (only in development)
        const err = error as any;
        const isSSLError = 
          err?.code === 'ERR_CERT_AUTHORITY_INVALID' ||
          err?.code === 'ERR_CERT_COMMON_NAME_INVALID' ||
          err?.code === 'CERT_HAS_EXPIRED' ||
          err?.message?.includes('certificate') ||
          err?.message?.includes('SSL') ||
          err?.message?.includes('Network Error');
        
        if (isSSLError && process.env.NODE_ENV === 'development') {
          console.warn('[Billing API] SSL Certificate Error (silently ignored):', {
            message: err?.message,
            code: err?.code,
            url: err?.config?.url || err?.config?.baseURL,
          });
        }
      }
    };

    // Defer non-critical API call to improve initial load time
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        void fetchPlanProfile();
      }, { timeout: 2000 });
    } else {
      setTimeout(() => {
        void fetchPlanProfile();
      }, 100);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const getNavigations = useCallback(
    (modules: string[], excludeScreens: string[] = []) => {
      const filterItems = [];
      navigation.items.forEach((item) => {
        if (modules.indexOf(item.module) >= 0 && excludeScreens.indexOf(item.screen) < 0) {
          if (planFeatures) {
            const parentFeatureKey = MODULE_FEATURE_MAP[item.module];
            if (parentFeatureKey && planFeatures[parentFeatureKey] === false) {
              return;
            }
          }

          if (item.children && item.children.length > 0) {
            const filterChildren = item.children
              .filter((child) => {
                if (modules.indexOf(child.module) < 0 || excludeScreens.indexOf(child.screen) >= 0) {
                  return false;
                }

                if (advancedReportsEnabled === false && ADVANCED_REPORT_SCREENS.includes(child.screen)) {
                  return false;
                }

                if (planFeatures) {
                  const featureKey = MODULE_FEATURE_MAP[child.module];
                  if (featureKey && planFeatures[featureKey] === false) {
                    return false;
                  }
                  const screenFeatureKey = SCREEN_FEATURE_MAP[child.screen];
                  if (screenFeatureKey && planFeatures[screenFeatureKey] === false) {
                    return false;
                  }
                }

                return true;
              })
              .map((child) => ({
                ...child,
                name: t(`navigation:${child.name}.name`),
                badge: { ...child.badge, text: t(`navigation:${child.name}.badge`) },
              }));
            if (filterChildren.length > 0) {
              filterItems.push({
                ...item,
                name: t(`navigation:${item.name}.name`),
                badge: { ...item.badge, text: t(`navigation:${item.name}.badge`) },
                children: filterChildren,
              });
            }
          } else {
            filterItems.push({
              ...item,
              name: t(`navigation:${item.name}.name`),
              badge: { ...item.badge, text: t(`navigation:${item.name}.badge`) },
            });
          }
        } else if (item.children && item.children.length > 0) {
          const filterChildren = item.children
            .filter((child) => {
              if (modules.indexOf(child.module) < 0 || excludeScreens.indexOf(child.screen) >= 0) {
                return false;
              }

              if (advancedReportsEnabled === false && ADVANCED_REPORT_SCREENS.includes(child.screen)) {
                return false;
              }

              if (planFeatures) {
                const featureKey = MODULE_FEATURE_MAP[child.module];
                if (featureKey && planFeatures[featureKey] === false) {
                  return false;
                }
                const screenFeatureKey = SCREEN_FEATURE_MAP[child.screen];
                if (screenFeatureKey && planFeatures[screenFeatureKey] === false) {
                  return false;
                }
              }

              return true;
            })
            .map((child) => ({
              ...child,
              name: t(`navigation:${child.name}.name`),
              badge: { ...child.badge, text: t(`navigation:${child.name}.badge`) },
            }));
          if (filterChildren.length > 0) {
            filterItems.push({
              ...item,
              name: t(`navigation:${item.name}.name`),
              badge: { ...item.badge, text: t(`navigation:${item.name}.badge`) },
              children: filterChildren,
            });
          }
        }
      });

      return { items: filterItems };
    },
    [advancedReportsEnabled, planFeatures, t]
  );

  const appRoutes = useMemo(() => {
    return routes.map((item) => ({
      ...item,
      name: t(`navigation:${item.name}.name`),
    }));
  }, [t]);

  return { getNavigations, appRoutes };
};
